/**
 * Draaidagtaak ↔ draaidag (23 okt 2026, project 9604 FBTO).
 *
 * Een draaidagtaak krijgt bij aanmaak de titel van de Outlook-afspraak als naam en de
 * genodigden als uitvoerder. Daarna volgt de taak de afspraak niet meer: de planner zet
 * "Erwin(b) + Paul(b) + Enya || FBTO - Expertvideo - [9604] - NK" in Outlook, de taak heet nog
 * "Henk + camera || …". Externe crew (freelancers zonder account) staat bovendien alleen in de
 * titel. Daarom toont de projectpagina bij een draaidagtaak de crew van de gekoppelde draaidag
 * (gekoppelde Personen + titelnamen met (b)/(g)/(?)) in plaats van taaknaam en uitvoerder.
 *
 * Koppeling, in volgorde van zekerheid:
 *  1. het link-veld op de taak (`DraaidagenOutlook`, Airtable/main);
 *  2. het link-veld op de draaidag (`Tasks`);
 *  3. zelfde startdatum binnen het project — alleen als er precies één actieve draaidag op die
 *     dag is (de PG-spiegel op develop kent de link niet).
 */
import { crewSamengevoegd } from './draaidagCrew';

const lijst = (v) => (Array.isArray(v) ? v : v ? [v] : []);
const puurDatum = (v) => {
  const s = String(v || '').slice(0, 10);
  return /^\d{4}-\d{2}-\d{2}$/.test(s) ? s : '';
};

// 4-laags filter (zie CLAUDE.md "Draaidagen"); ontbrekende velden laten hem staan.
function isActief(f = {}) {
  // `_actief` = genormaliseerd door de Node-route op main (Airtable laat lege vinkjes weg).
  if (f._actief === false) return false;
  if (f.is_valid === false) return false;
  if (f.all_day === false) return false;
  if (f.hidden === true || f.Verberg === true) return false;
  if (f.outlook_link_status === false || f.OutlookLinkStatus === false) return false;
  return true;
}

// Pure datum van de draaidag: StartDatum (Airtable-formule) of de Outlook-start in Amsterdam.
export function draaidagDatum(d) {
  const f = d?.fields || {};
  const puur = puurDatum(f.StartDatum);
  if (puur) return puur;
  const ruw = f.StartdatumOutlook;
  if (!ruw) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(String(ruw))) return String(ruw);
  const dt = new Date(ruw);
  if (Number.isNaN(dt.getTime())) return '';
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Amsterdam' }).format(dt);
  } catch {
    return puurDatum(ruw);
  }
}

/** De draaidag die bij deze taak hoort, of null (ook voor taken die geen draaidagtaak zijn). */
export function draaidagVoorTaak(task, draaidagen) {
  const f = task?.fields || {};
  if (f.Type !== 'Draaidag' || !Array.isArray(draaidagen) || !draaidagen.length) return null;
  const links = lijst(f.DraaidagenOutlook);
  // Ook een expliciet gekoppelde draaidag moet nog doorgaan (verborgen/geschrapt → geen crew).
  const viaTaak = links.length ? draaidagen.find((d) => links.includes(d.id)) : null;
  if (viaTaak) return isActief(viaTaak.fields) ? viaTaak : null;
  const viaDag = draaidagen.find((d) => lijst(d.fields?.Tasks).includes(task.id));
  if (viaDag) return isActief(viaDag.fields) ? viaDag : null;
  if (links.length) return null; // gekoppeld aan een draaidag die hier niet staat: niet gokken
  const dag = puurDatum(f.StartDateUitvoerder);
  if (!dag) return null;
  const opDag = draaidagen.filter((d) => isActief(d.fields) && draaidagDatum(d) === dag);
  return opDag.length === 1 ? opDag[0] : null;
}

/**
 * Weergave voor een draaidagtaak: { naam, crew, titel, draaidag } of null.
 * `naam` = "Draaidag · Enya + Erwin (b) + Paul (b)"; `titel` = de actuele Outlook-titel.
 */
export function draaidagTaakWeergave(task, draaidagen) {
  const d = draaidagVoorTaak(task, draaidagen);
  if (!d) return null;
  const titel = String(d.fields?.Draaidag || '').trim();
  const { label } = crewSamengevoegd(d.fields?.['Naam (from Crew)'], titel);
  if (!label && !titel) return null;
  return {
    naam: label ? `Draaidag · ${label}` : titel,
    crew: label,
    titel,
    draaidag: d,
  };
}

export default draaidagTaakWeergave;
